import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import NavBar from '../components/nav';
import { Footer } from '../components/footer';
import namkeLogoHeader from '../assets/namke-logo-header.png';
import API_BASE_URL from '../config/api';

const inputClass =
  'w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-700/25 focus:border-primary-700 transition-colors';

export default function ResetPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!tokenFromUrl) {
      setError(t('auth.invalidIncompleteLink'));
    }
  }, [tokenFromUrl, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError(t('auth.passwordMinLength'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.passwordsMismatch'));
      return;
    }
    if (!tokenFromUrl) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tokenFromUrl, new_password: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(typeof data.detail === 'string' ? data.detail : t('auth.invalidResetLink'));
        return;
      }
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true, state: { message: t('auth.passwordUpdatedLoginMessage') } }), 2000);
    } catch {
      setError(t('auth.cannotContactServer'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-namke-white">
      <NavBar />

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-[rgba(255,199,23,0.12)]">
          <div
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 20%, rgb(114, 183, 68) 0%, transparent 45%), radial-gradient(circle at 80% 60%, rgb(5, 70, 37) 0%, transparent 40%)',
            }}
          />
          <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20 py-16">
            <h2 className="title-font text-4xl xl:text-5xl font-bold leading-tight mb-4 text-primary-700">
              {t('auth.newPasswordTitle')}
            </h2>
            <p className="text-lg text-gray-900 font-normal max-w-lg">
              {t('auth.choosePasswordDesc')}
            </p>
          </div>
        </div>

        <div className="w-full lg:w-[45%] flex items-start justify-center pt-10 lg:pt-16 pb-12 px-6 sm:px-8">
          <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200/90 p-8">
            <div className="flex justify-center mb-6">
              <div className="w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center ring-1 ring-primary-200/60">
                <img src={namkeLogoHeader} alt="namke" className="h-16 w-auto object-contain" />
              </div>
            </div>

            <h1 className="title-font text-3xl font-bold text-primary-700 text-center mb-2">{t('auth.resetTitle')}</h1>
            <p className="text-gray-900 font-normal text-center mb-8 text-sm">{t('auth.setNewPasswordDesc')}</p>

            {success ? (
              <div className="form-message form-message--success text-center text-sm">
                {t('auth.passwordSavedRedirecting')}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && <div className="form-message form-message--error text-center text-sm">{error}</div>}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auth.newPassword')}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      className={`${inputClass} pr-11`}
                      placeholder={t('auth.atLeast8Chars')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-700"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label htmlFor="confirm" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('auth.confirmNewPassword')}
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="confirm"
                    className={inputClass}
                    placeholder={t('auth.repeatPassword')}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !tokenFromUrl}
                  className="w-full bg-primary-700 text-white py-3 px-4 rounded-xl hover:bg-primary-800 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t('auth.saving') : t('auth.savePassword')}
                </button>
                <p className="text-center text-sm text-gray-900">
                  <Link to="/login" className="font-semibold text-primary-700 hover:text-primary-800">
                    {t('auth.backToLogin2')}
                  </Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
